import { Component } from '@angular/core';
import { FormBuilder } from '@angular/forms';

import _ from 'lodash';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';

import FileSaver from 'file-saver';
import moment from 'moment-timezone';
import { GraphqlService } from 'src/app/graphql/graphql.service';
import { Contract } from 'src/app/shared/models';
import { AbstractPageForm } from 'src/app/shared/pages/page-form.abstract';
import { generateLoaLoeValidator } from 'src/app/shared/validators/generate-loa-loe.validator';
import { HelperService } from '../../helper.service';

type GenerateLoaLoeFileOption = 'download' | 'send';

@Component({
	selector: 'pk-broker-generate-loa-loe-modal',
	templateUrl: './generate-loa-loe-modal.component.html',
	styleUrls: ['./generate-loa-loe-modal.component.scss'],
	standalone: false,
})
export class GenerateLoaLoeModalComponent extends AbstractPageForm {
	// inputs
	public contract: Contract;

	// outputs
	public submitted: Subject<{
		addLOA: GenerateLoaLoeFileOption;
		addLOE: GenerateLoaLoeFileOption;
	}> = new Subject();

	public latestLoaSentDate: Date;
	public latestLoaDownloadedDate: Date;
	public latestLoeSentDate: Date;
	public latestLoeDownloadedDate: Date;

	constructor(
		private fb: FormBuilder,
		private activeModal: BsModalRef,
		private graphqlService: GraphqlService,
		private toastrService: ToastrService,
		private helperService: HelperService,
	) {
		super();
		this.setSubmitText('Generate', 'Generating...');
	}

	public async loadPageData(): Promise<void> {
		this.latestLoaSentDate = this.contract.latestLoaSentDate();
		this.latestLoaDownloadedDate = this.contract.latestLoaDownloadedDate();
		this.latestLoeSentDate = this.contract.latestLoeSentDate();
		this.latestLoeDownloadedDate = this.contract.latestLoeDownloadedDate();
	}

	public getForm() {
		return this.fb.group(
			{
				addLogo: false,
				addLOA: null as GenerateLoaLoeFileOption,
				addLOE: null as GenerateLoaLoeFileOption,
			},
			{ validators: generateLoaLoeValidator },
		);
	}

	get hasDownload() {
		return this.form.addLOA.value === 'download' || this.form.addLOE.value === 'download';
	}

	protected async onFormSubmitted() {
		try {
			await this.graphqlService.generateLOALOE({
				contractId: this.contract.contractId,
				addLOA: this.form.addLOA.value,
				addLOE: this.form.addLOE.value,
				addLogo: this.form.addLOE.value && this.form.addLogo.value,
			});
		} catch (e) {
			this.toastrService.warning('LOA/LOE template was not found. Please contact support.', 'Contract');
			return;
		}

		if (this.hasDownload) {
			try {
				const result = await this.graphqlService.getContractEsignDocuments(this.contract.contractId);

				const types = [];
				if (this.form.addLOA.value === 'download') {
					types.push(this.CONSTANTS.attachmentTypes.loa);
				}
				if (this.form.addLOE.value === 'download') {
					types.push(this.CONSTANTS.attachmentTypes.loe);
				}

				for (const type of types) {
					const esigns = _.chain(result.data.esigns.message)
						.filter(e => e.documents?.some(d => d.document?.attachment?.attachmentTypeId === type))
						.orderBy(['addDate'], ['desc'])
						.valueOf();
					const esign = esigns.length ? esigns[0] : null;
					const document = esign?.documents?.find(d => d.document?.attachment?.attachmentTypeId === type);

					if (document) {
						const documentResult = await this.graphqlService.getDocumentBase64(document.documentId, {
							contractId: this.contract.contractId,
							signature: esign.signature,
							initials: esign.initials,
							signatureDate: moment().format('YYYY-MM-DDTHH:mm:ss.SSS'),
							inkSignature: true,
							addLogo: type === this.CONSTANTS.attachmentTypes.loe && this.form.addLogo.value,
						});
						if (documentResult?.data.documentBase64) {
							const binary = window.atob(documentResult.data.documentBase64.response);
							const ab = this.helperService.s2ab(binary);
							const blob = new Blob([ab], { type: 'data:application/pdf;' });
							if (window.navigator.msSaveOrOpenBlob) {
								window.navigator.msSaveOrOpenBlob(blob, document.document.name + '.pdf');
							} else {
								FileSaver.saveAs(blob, document.document.name + '.pdf');
							}
						}
					} else {
						this.toastrService.warning(
							`We could not generate the document to download. We have been notified and ` + `are working to fix the issue.`,
							'Contract',
						);
					}
				}
			} catch (e) {
				this.toastrService.warning(
					`We could not generate the document to download. We have been notified and ` + `are working to fix the issue.`,
					'Contract',
				);
			}
		}

		this.submitted.next({
			addLOA: this.form.addLOA.value,
			addLOE: this.form.addLOE.value,
		});
		this.close();
	}

	public close(): void {
		this.activeModal.hide();
	}
}
